import * as React from "react";
import ProfileList from "./ProfileList"
import Pagination from "../../../shared/search/Pagination"
import ProfileFilters from "./ProfileFilters"

let isMobile = false;
if (typeof window !== 'undefined') {
  const { isMobile: _isMobile } = require('react-device-detect');
  isMobile = _isMobile;
}

if (typeof window !== 'undefined') {
  const { dotStream } = require('ldrs');
  dotStream.register();
}

const URI = "/profilesearch.json"

interface Profile {
  id: number;
  [key: string]: any;
}

interface JobType {
  id: number;
  name: string;
  [key: string]: any;
}

interface Skill {
  id: number;
  name: string;
  [key: string]: any;
}

interface SkillCategory {
  id: number;
  name: string;
  skills: Skill[];
  [key: string]: any;
}

interface FormElements {
  position_types_id_in: string[];
  skills_id_in: string[];
  favorites: boolean;
  job_types_group_eq: string;
  hourly_rate_gteq: string;
  hourly_rate_lteq: string;
  [key: string]: any;
}

interface ProfilesProps {
  form_elements: any;
  job_types: any[];
  skill_categories: any[];
  skills: any[];
  authenticity_token: string;
  favorites: any[];
  blocked: boolean;
}

const Profiles: React.FC<ProfilesProps> = (props) => {
  const { job_types, skill_categories, skills, authenticity_token } = props;

  const [profiles, setProfiles] = React.useState<Profile[]>([]);
  const [formElements, setFormElements] = React.useState<FormElements>({
    ...props.form_elements,
    position_types_id_in: [],
    skills_id_in: [],
    favorites: false,
    job_types_group_eq: "",
    hourly_rate_gteq: "",
    hourly_rate_lteq: "",
  });
  const [sort, setSort] = React.useState("newest");
  const [location, setLocation] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [miles, setMiles] = React.useState(25);
  const [loaded, setLoaded] = React.useState(false);
  const [favorites, setFavorites] = React.useState(props.favorites);
  const [blocked, setBlocked] = React.useState(props.blocked);
  const [currentPage, setCurrentPage] = React.useState<number | undefined>(undefined);
  const [totalPages, setTotalPages] = React.useState<number | undefined>(undefined);
  const [records, setRecords] = React.useState<number | undefined>(undefined);
  const [prevPage, setPrevPage] = React.useState<number | undefined>(undefined);
  const [nextPage, setNextPage] = React.useState<number | undefined>(undefined);

  const sendQuery = React.useCallback(() => {
    const body = JSON.stringify({
      page,
      sort,
      location,
      miles,
      search: { ...formElements },
    });
    const headers = {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": authenticity_token,
    };

    const showEmpty = () => {
      setProfiles([]);
      setLoaded(true);
    };

    fetch(URI, { method: "POST", headers, body })
      .then(async res => {
        // An expired session or an outage answers with devise JSON or an
        // HTML page, not a result set. Show an empty list instead of
        // crashing on profiles.map (Sentry DIRECTDENTAL-215).
        const isJson = (res.headers.get("content-type") || "").includes("application/json");
        const data = res.ok && isJson ? await res.json() : null;
        if (!data || !Array.isArray(data.profiles)) {
          showEmpty();
          return;
        }

        const { profiles, total_pages, records, favorites } = data;
        const current_page = data["current_page="];
        const prev_page = current_page === 1 ? current_page : current_page - 1;
        const next_page = current_page === total_pages ? current_page : current_page + 1;

        setProfiles(profiles);
        setCurrentPage(current_page);
        setTotalPages(total_pages);
        setRecords(records);
        setPrevPage(prev_page);
        setNextPage(next_page);
        setLoaded(true);
        if (Array.isArray(favorites)) setFavorites(favorites);
      })
      .catch(showEmpty);
  }, [page, sort, location, miles, formElements, authenticity_token]);

  React.useEffect(() => {
    sendQuery();
  }, [sendQuery]);

  const onChangeCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = event.target;
    let new_position_types_id_in = [...formElements.position_types_id_in];
    let new_skills_id_in = [...formElements.skills_id_in];

    if (checked) {
      if (name === "position_types_id_in" && !new_position_types_id_in.includes(value)) {
        new_position_types_id_in.push(value);
      }
      if (name === "skills_id_in" && !new_skills_id_in.includes(value)) {
        new_skills_id_in.push(value);
      }
    } else {
      if (name === "position_types_id_in") {
        new_position_types_id_in = new_position_types_id_in.filter(v => v !== value);
      }
      if (name === "skills_id_in") {
        new_skills_id_in = new_skills_id_in.filter(v => v !== value);
      }
    }

    setFormElements({
      ...formElements,
      position_types_id_in: new_position_types_id_in,
      skills_id_in: new_skills_id_in,
    });
  };

  const onChangeLocation = (event: any) => {
    const { name, value } = event.target;

    if (name === "miles") {
      setMiles(parseInt(value));
      return;
    }

    if (name === "location") {
      const re = /^[0-9\b]+$/;
      const isZipCode = value.length <= 5 && (value === "" || re.test(value));
      if (isZipCode) {
        setLocation(value);
      }
    }
  };

  const onChangeSort = (event: any) => {
    setSort(event.target.value);
  };

  const setForm = (event: any) => {
    const { name, value } = event.target;
    setFormElements({ ...formElements, [name]: value });
    setPage(1);
  };

  const navigateTo = (newPage: number) => {
    setPage(newPage);
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 100);
    }
  };

  const onToggleFavorites = (e: any) => {
    const newFavorites = !formElements.favorites;
    setFormElements({
      ...formElements,
      favorites: newFavorites,
    });
    setPage(1);
    setCurrentPage(1);
  };

  const key = profiles.map(profile => profile.id).join(",");

  return (
    <div className="container browsing-font mt-5 px-4">
      {formElements.favorites && favorites.length < 1 && (
        <div style={{
          background: 'linear-gradient(to bottom, rgba(8, 94, 200, 0.05), rgba(23, 111, 201, 0.02))',
          border: '1px solid #e8ecef',
          borderRadius: '0.75rem',
          padding: '2rem',
          marginBottom: '2rem',
          textAlign: 'center'
        }}>
          <i className="fa fa-heart" style={{ fontSize: '3rem', color: '#6c757d', marginBottom: '1rem' }}></i>
          <h4 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#495057', marginBottom: '0.5rem' }}>
            No Favorites Yet
          </h4>
          <p style={{ fontSize: '1rem', color: '#6c757d' }}>
            Click the <i className="fa fa-heart" style={{ color: '#dc3545' }}></i> on any
            candidate's profile to save them as a favorite.
          </p>
        </div>
      )}

      {isMobile && (
        <div className="offset-3 my-4">
          <button
            type="button"
            className="btn-modern btn-modern--primary"
            data-bs-toggle="modal"
            data-bs-target="#exampleModal"
          >
            <i className="fa fa-filter me-2"></i>
            Search Filters
          </button>

          <div
            className="modal fade px-0"
            id="exampleModal"
            tabIndex={-1}
            aria-labelledby="exampleModalLabel"
            aria-hidden="true"
          >
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" id="exampleModalLabel">
                    <i className="fa fa-filter me-2"></i>
                    Search Filters
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  <ProfileFilters
                    form_elements={formElements}
                    job_types={job_types}
                    location={location}
                    miles={miles.toString()}
                    skills={skills}
                    skill_categories={skill_categories}
                    onToggleFavorites={onToggleFavorites}
                    onChangeSort={onChangeSort}
                    onChangeLocation={onChangeLocation}
                    onChangeCheckbox={onChangeCheckbox}
                    onChange={setForm}
                  />
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn-modern btn-modern--primary"
                    data-bs-dismiss="modal"
                  >
                    <i className="fa fa-check me-2"></i>
                    Apply Filters
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="row">
        <div
          className="col-12 col-lg-8 mb-4"
          id="job-list"
          key={key}
        >
          <div style={{
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '14px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            overflow: 'hidden'
          }}>
            {/* Header */}
            <div style={{
              padding: '1.25rem 1.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: 'linear-gradient(135deg, #085ec8 0%, #0a6fd9 50%, #085ec8 100%)',
              position: 'relative',
              overflow: 'hidden',
            }}>
              {/* Subtle texture overlay */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(255,255,255,0.08) 0%, transparent 40%)',
                pointerEvents: 'none',
              }} />
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                position: 'relative',
                zIndex: 1,
              }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '10px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <i className="fas fa-users" style={{ color: 'white', fontSize: '16px' }}></i>
                </div>
                <h2 style={{
                  margin: 0,
                  fontSize: '1.125rem',
                  fontWeight: 700,
                  color: 'white',
                  letterSpacing: '-0.01em',
                }}>
                  Candidates
                </h2>
              </div>
            </div>
            {profiles.length > 0 && (
              <ProfileList
                authenticity_token={authenticity_token}
                profiles={profiles}
                favorites={favorites}
              />
            )}
            {profiles.length < 1 && loaded && <NoResults />}
            {profiles.length < 1 && !loaded && <LoadingResults />}
          </div>
        </div>

        <div className="col-12 col-lg-4" id="job-filters">
          {!isMobile && (
            <ProfileFilters
              form_elements={formElements}
              job_types={job_types}
              location={location}
              miles={miles.toString()}
              skills={skills}
              skill_categories={skill_categories}
              onToggleFavorites={onToggleFavorites}
              onChangeSort={onChangeSort}
              onChangeLocation={onChangeLocation}
              onChangeCheckbox={onChangeCheckbox}
              onChange={setForm}
            />
          )}
        </div>
      </div>
      {profiles.length > 0 && (
        <Pagination
          current_page={currentPage}
          total_pages={totalPages}
          records={records}
          prev_page={prevPage}
          next_page={nextPage}
          navigateTo={navigateTo}
        />
      )}
    </div>
  );
};

export default Profiles;

function LoadingResults() {
  return (
    <div style={{
      padding: '5rem 2rem',
      textAlign: 'center',
      background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
    }}>
      <div style={{
        width: '64px',
        height: '64px',
        margin: '0 auto 1.5rem',
        borderRadius: '16px',
        background: 'linear-gradient(135deg, #085ec8, #0a6fd9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 4px 16px rgba(8, 94, 200, 0.25)',
      }}>
        <l-dot-stream
          size="32"
          speed="2.5"
          color="white"
        ></l-dot-stream>
      </div>
      <h4 style={{
        color: '#1a365d',
        fontWeight: 700,
        marginBottom: '0.5rem',
        fontSize: '1.25rem',
        letterSpacing: '-0.01em',
      }}>
        Finding Candidates...
      </h4>
      <p style={{
        color: '#64748b',
        margin: 0,
        fontSize: '0.9375rem',
      }}>
        Searching for the best matches
      </p>
    </div>
  );
}

function NoResults() {
  return (
    <div style={{
      padding: '5rem 2rem',
      textAlign: 'center',
      background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
    }}>
      <div style={{
        width: '80px',
        height: '80px',
        margin: '0 auto 1.5rem',
        borderRadius: '20px',
        background: '#f1f5f9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <i className="fas fa-user-slash" style={{ fontSize: '32px', color: '#94a3b8' }}></i>
      </div>
      <h4 style={{
        color: '#1a365d',
        fontWeight: 700,
        marginBottom: '0.75rem',
        fontSize: '1.25rem',
        letterSpacing: '-0.01em',
      }}>
        No Candidates Found
      </h4>
      <p style={{
        color: '#64748b',
        maxWidth: '400px',
        margin: '0 auto 1.75rem',
        lineHeight: 1.6,
        fontSize: '0.9375rem',
      }}>
        We couldn't find candidates matching your criteria. Try adjusting your filters or{" "}
        <a
          href="/jobs/new"
          style={{
            color: '#085ec8',
            textDecoration: 'none',
            fontWeight: 600,
          }}
        >
          post a job
        </a>{" "}
        to reach our full network.
      </p>
      <a
        href="/profiles"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.75rem 1.5rem',
          borderRadius: '10px',
          fontWeight: 600,
          fontSize: '0.9375rem',
          textDecoration: 'none',
          background: 'linear-gradient(135deg, #085ec8, #0a6fd9)',
          color: 'white',
          border: 'none',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          boxShadow: '0 4px 12px rgba(8, 94, 200, 0.3)',
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(8, 94, 200, 0.4)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(8, 94, 200, 0.3)';
        }}
      >
        <i className="fas fa-redo"></i>
        Reset Filters
      </a>
    </div>
  );
}
